import { Component, OnDestroy, OnInit, inject, signal, effect } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Observable, Subject, filter, takeUntil, tap } from 'rxjs';
import { AwareSecurityService } from '@appbolaget/aware-security';
import { SpotlightComponent } from '@components/spotlight/spotlight.component';
import { SidenavComponent, TopbarComponent } from '@components/index';
import { AsyncPipe } from '@angular/common';
import { AwareAuthIdleService, AwareAuthLogoutReason, AwareAuthService } from '@appbolaget/aware-auth';
import { DialogService } from '@viewservices/dialog.service';
import { MatDialogRef } from '@angular/material/dialog';
import { AwareAuthIdleWarningComponent } from '@modules/auth/components/idle-warning/idle-warning.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
    selector: 'app-pages',
    templateUrl: './pages.component.html',
    styleUrls: ['./pages.component.scss'],
    imports: [SpotlightComponent, SidenavComponent, AsyncPipe, TopbarComponent, RouterOutlet],
})
export class Pages implements OnInit, OnDestroy {
    router = inject(Router);

    canViewSidenav$: Observable<boolean>;
    destroy$: Subject<void>;

    // Mobile responsive state
    isMobile = signal<boolean>(false);
    sidenavMode = signal<'side' | 'over'>('side');
    sidenavOpen = signal<boolean>(true);
    isCollapsed = signal<boolean>(false); // Track collapsed state for tablet
    enableAnimation = signal<boolean>(false); // Enable animation only for manual toggles

    #awareSecurityService = inject(AwareSecurityService);
    #awareAuthService = inject(AwareAuthService);
    #awareAuthIdleService = inject(AwareAuthIdleService);
    #dialogService = inject(DialogService);
    #breakpointObserver = inject(BreakpointObserver);

    constructor() {
        // Auto-open sidenav on desktop, auto-close on mobile
        effect(() => {
            const mode = this.sidenavMode();
            if (mode === 'side') {
                this.sidenavOpen.set(true);
            } else if (mode === 'over') {
                this.sidenavOpen.set(false);
            }
        });
    }

    async ngOnInit() {
        this.destroy$ = new Subject();
        this.canViewSidenav$ = this.#awareSecurityService.isGranted('sidemenu.read');

        // Setup breakpoint observer for mobile, tablet, and desktop
        this.#breakpointObserver
            .observe([Breakpoints.XSmall, Breakpoints.Small])
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const isXSmall = this.#breakpointObserver.isMatched(Breakpoints.XSmall);
                const isSmall = this.#breakpointObserver.isMatched(Breakpoints.Small);

                // Disable animation during breakpoint changes
                this.enableAnimation.set(false);

                if (isXSmall) {
                    // Mobile: overlay mode with full sidenav when open
                    this.isMobile.set(true);
                    this.sidenavMode.set('over');
                    this.isCollapsed.set(false);
                } else if (isSmall) {
                    // Tablet: side mode with collapsed (icon-only) sidenav
                    this.isMobile.set(false);
                    this.sidenavMode.set('side');
                    this.isCollapsed.set(true);
                } else {
                    // Desktop: side mode with full sidenav
                    this.isMobile.set(false);
                    this.sidenavMode.set('side');
                    this.isCollapsed.set(false);
                }
            });

        let awareAuthIdleDialog: MatDialogRef<AwareAuthIdleWarningComponent>;

        this.#awareAuthIdleService.onIdleWarning$
            .pipe(
                takeUntil(this.destroy$),
                filter(() => !awareAuthIdleDialog),
                tap(() => {
                    awareAuthIdleDialog = this.#dialogService.open(AwareAuthIdleWarningComponent, {
                        width: '95%',
                        maxWidth: '500px',
                        disableClose: true,
                    });

                    awareAuthIdleDialog.afterClosed().subscribe(() => {
                        awareAuthIdleDialog = null;
                    });
                }),
            )
            .subscribe();

        this.#awareAuthIdleService.onTimeout$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.#awareAuthService.logout({
                        reason: AwareAuthLogoutReason.idle,
                        message: 'Du har blivit utloggad på grund av inaktivitet.',
                    });
                }),
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleSidenav() {
        if (this.isMobile()) {
            this.enableAnimation.set(true);
        }
        this.sidenavOpen.update((val) => !val);
    }

    closeSidenav() {
        if (this.isMobile()) {
            this.enableAnimation.set(true);
        }
        this.sidenavOpen.set(false);
    }

    onInactive() {
        // this.auth.signout();
        // this.router.navigate(['/login']);
    }
}
